<template>
  <!-- Mod -->
  <img :src="modIconSrc(task)" class="w-4 h-4 mr-1">
  <span class="w-48 flex-shrink-0 truncate">
    {{ task.name ?? task.mod.friendly_name }}
    <span v-if="task.is_exam">{{ $t('training.task.exam_suffix') }}</span>
  </span>

  <!-- Duration -->
  <span
    class="w-14 text-center flex-shrink-0"
    :title="`${moment(task.completion.started_at).format('hh:mm:ss a')} - ${moment(task.completion.ended_at).format('hh:mm:ss a')}`"
    v-if="task.completion && moment.duration(moment(task.completion.ended_at).diff(task.completion.started_at)).asSeconds() < 60">
    {{ $t('training.task.seconds_short', { n: Math.floor(moment.duration(moment(task.completion.ended_at).diff(task.completion.started_at)).asSeconds()) }) }}
  </span>
  <span
    class="w-14 text-center flex-shrink-0"
    :title="`${moment(task.completion.started_at).format('hh:mm a')} - ${moment(task.completion.ended_at).format('hh:mm a')}`"
    v-else-if="task.completion">
    {{ $t('training.task.minutes_short', { n: Math.round(moment.duration(moment(task.completion.ended_at).diff(task.completion.started_at)).asMinutes()) }) }}
  </span>
  <span v-else class="w-14 text-center flex-shrink-0">
    -
  </span>

  <!-- Completion -->
  <span class="w-24 flex-shrink-0 flex flex-row items-center justify-center">
    <CheckIcon v-if="task.completion?.status == 'passed' && !task.is_exam" class="v-4 h-4 text-green-400" />
    <NoSymbolIcon v-else-if="task.completion?.status == 'failed' && !task.is_exam" class="v-4 h-4 text-red-400" />
    <CheckCircleIcon v-else-if="task.completion?.status == 'passed' && task.is_exam" class="v-4 h-4 text-green-400" />
    <XCircleIcon v-else-if="task.completion?.status == 'failed' && task.is_exam" class="v-4 h-4 text-red-400" />
    <EllipsisHorizontalIcon v-else-if="live" class="v-4 h-4 text-text-secondary animate-pulse" />
    <NoSymbolIcon v-else class="v-4 h-4 text-red-400" />

    <span v-if="task.is_exam && task.completion?.status == 'passed'" class="text-xs ml-1 text-green-400">
      {{ $t('training.task.passed') }}
    </span>
    <span v-else-if="task.is_exam && task.completion?.status == 'failed'" class="text-xs ml-1 text-red-400">
      {{ $t('training.task.failed') }}
    </span>
    <span
      v-else-if="['arenas', 'kills'].includes(task.completion_type)"
      :class="{
        'text-xs ml-1': true,
        'text-green-400': task.completion?.status == 'passed',
        'text-text-secondary': task.completion?.status != 'passed' && live,
        'text-red-400': task.completion?.status != 'passed' && !live,
      }"
    >
      {{ $t(`training.task.unit_${task.completion_type}`, Number(task.completion_value)) }}
    </span>
  </span>

  <!-- Map (with picture from `public` folder) -->
  <span class="w-8 sm:w-24 text-left flex flex-shrink-0 gap-1 items-center">
    <img :src="`/maps/map_icon_${task.cs_map.name}.svg`" class="h-4 w-4" />
    <span class="text-sm hidden sm:inline ">{{ task.cs_map.friendly_name }}</span>
  </span>

  <!-- Weapon -->
  <span v-if="task.statistics[0]?.weapon_name && !task.mod.friendly_name.match(/^pop/i)" class="w-16 flex-shrink-0 text-center">
    <img v-show="task.statistics[0].weapon_name" :src="equipmentIconSrc(task.statistics[0].weapon_name)" class="inline h-4">
  </span>
  <span v-else class="w-16 flex-shrink-0" />

  <!-- Elo -->
  <span v-if="hideElo" class="w-24 flex-shrink-0" />
  <span v-else-if="task.bot_mode == 'practice'" class="w-24 flex-shrink-0" />
  <span v-else-if="task.bot_mode == 'competitive' && task.high_score" class="w-24 flex-shrink-0 text-sm flex justify-center items-center gap-1">
    {{ highScoreValue() }}
  </span>
  <span v-else-if="(task.bot_mode == 'smart' || !task.bot_mode) && modHasElo(task) && (task.completion?.elo_end ?? -1) >= 0" class="w-24 flex-shrink-0 text-sm flex justify-center items-center gap-1">
    {{ task.completion.elo_end.toLocaleString('en-US') }}
    <span
      v-if="(task.completion?.elo_start ?? -1) >= 0"
      class="rounded py-[2px] px-[3px] text-xs"
      :class="taskEloClass(task)"
    >{{ task.completion.elo_end >= task.completion.elo_start ? '+' : '' }}{{ task.completion.elo_end - task.completion.elo_start }}
    </span>
  </span>
  <span v-else class="w-24 flex-shrink-0" />

  <!-- Bot mode -->
  <span v-if="task.bot_mode == 'competitive'" :class="botModeClass()">{{ $t('training.task.bot_mode_competitive') }}</span>
  <span v-else-if="task.bot_mode == 'practice'" :class="botModeClass()">{{ $t('training.task.bot_mode_practice') }}</span>
  <span v-else-if="task.completion" :class="botModeClass()">{{ $t('training.task.bot_mode_smart') }}</span>

  <!-- Metrics -->
  <div class="ml-4 space-x-2 hidden lg:inline">
    <span
      class="text-sm whitespace-nowrap"
      v-for="metric in relevantMetrics(task)"
      :class="`${selectedMetric && (metric.key == selectedMetric.key) ? 'text-text-primary' : 'text-text-secondary'}`"
    >
      <span class="has-tooltip relative">
        <img :src="metric.imageSrc" class="h-4 inline task-row-metric-img" :class="`${selectedMetric && (metric.key === selectedMetric.key) ? 'brightness-200' : ''}`">
        <span class="tooltip absolute rounded shadow-lg p-1 bg-gray-100 text-black text-xs ml-1 -mt-6">{{ metric.tooltip }}</span>
        <span class="ml-1 mr-2 cursor-default">{{ metric.value }}</span>
      </span>
    </span>
  </div>
</template>

<script setup>
import moment from 'moment';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  CheckIcon, CheckCircleIcon, XCircleIcon, NoSymbolIcon, EllipsisHorizontalIcon,
} from '@heroicons/vue/20/solid';
import { formatDataString, formatStatStringData } from '../../utils/formatters.js';

const { t } = useI18n();
const props = defineProps(['live', 'task', 'hideElo', 'modCategories', 'selectedMetric']);

function equipmentIconSrc(weapon_name) {
  return new URL(`../../assets/equipment/${weapon_name?.replace('weapon_', '')}.svg`, import.meta.url).href;
}

function modIconSrc(task) {
  const category = findModCategory(task);
  return new URL(`../../assets/categories/circle/${category?.slug ?? 'misc'}.svg`, import.meta.url).href;
}

function totalKills(statistic) {
  if (props.task.completion_type !== 'kills' && statistic?.total_kills) {
    return {
      imageSrc: new URL('../../assets/icons/total_kills.svg', import.meta.url).href,
      tooltip: t('training.task.total_kills'),
      value: statistic.total_kills,
    };
  }
}

function headshotPercentage(statistic) {
  if (statistic?.total_headshots && statistic?.total_bodyshots && (statistic.total_headshots + statistic.total_bodyshots) > 0) {
    const headshot = statistic.total_headshots / (statistic.total_headshots + statistic.total_bodyshots);
    return {
      key: 'headshot_percentage',
      imageSrc: new URL('../../assets/icons/headshot_percentage.svg', import.meta.url).href,
      tooltip: t('training.task.headshot_percentage'),
      value: formatStatStringData(headshot, 'percentage'),
    };
  }
}

function accuracyPercentage(statistic) {
  const key = 'accuracy';
  const imageSrc = new URL('../../assets/icons/accuracy.svg', import.meta.url).href;
  const tooltip = t('training.task.accuracy');

  if (statistic?.total_headshots && statistic?.total_bodyshots && statistic?.total_bullets_fired > 0) {
    const accuracy = Math.min(1, (statistic.total_headshots + statistic.total_bodyshots) / statistic.total_bullets_fired);
    return {
      key,
      imageSrc,
      tooltip,
      value: formatStatStringData(accuracy, 'percentage'),
    };
  } if (statistic?.total_kills && statistic?.total_bullets_fired > 0) {
    // Special case for "Pop" mode
    return {
      key,
      imageSrc,
      tooltip,
      value: formatStatStringData(Math.min(1, statistic.total_kills / statistic.total_bullets_fired), 'percentage'),
    };
  }
}

function averageCounterStrafing(statistic) {
  if (statistic?.average_counter_strafe) {
    return {
      key: 'counter_strafing',
      imageSrc: new URL('../../assets/icons/counter-strafing.svg', import.meta.url).href,
      tooltip: t('training.task.counter_strafing'),
      value: formatStatStringData(statistic.average_counter_strafe / 100, 'percentage'),
    };
  }
}

function crosshairPlacement(statistic) {
  if (statistic?.average_crosshair_drift) {
    return {
      key: 'crosshair_placement',
      imageSrc: new URL('../../assets/icons/crosshair.svg', import.meta.url).href,
      tooltip: t('training.task.crosshair_placement'),
      value: formatStatStringData(statistic.average_crosshair_drift, 'degrees'),
    };
  }
}

function averageTimeToKill(statistic) {
  if (statistic?.average_time_to_kill) {
    return {
      key: 'time_to_kill',
      imageSrc: new URL('../../assets/icons/time_to_kill.svg', import.meta.url).href,
      tooltip: t('training.task.time_to_kill'),
      value: formatStatStringData(statistic.average_time_to_kill, 'seconds'),
    };
  }
}

function averageReactionTime(statistic) {
  if (statistic?.average_reaction_time) {
    return {
      key: 'reaction_time',
      imageSrc: new URL('../../assets/icons/reaction_time.svg', import.meta.url).href,
      tooltip: t('training.task.reaction_time'),
      value: formatStatStringData(statistic.average_reaction_time, 'seconds'),
    };
  }
}

function killsPerMinute(statistic) {
  const imageSrc = new URL('../../assets/icons/kills_per_minute.svg', import.meta.url).href;
  const tooltip = t('training.task.kills_per_minute');

  if (statistic?.kills_per_minute > 0) {
    return {
      imageSrc,
      tooltip,
      value: statistic.kills_per_minute,
    };
  }

  if (!props.task.completion?.started_at || !props.task.completion?.ended_at) {
    return null;
  }

  // NOTE(ssaunier): Fallback to computing KPM from task completion bounds & total kills
  const duration = moment(props.task.completion.ended_at).diff(moment(props.task.completion.started_at), 'minutes');
  if (statistic?.total_kills && duration > 0) {
    return {
      imageSrc,
      tooltip,
      value: Math.round(statistic.total_kills / duration),
    };
  }
}

const METRICS_MAPPING = {
  // slug   : [functions]
  _default: [totalKills, headshotPercentage, crosshairPlacement, averageTimeToKill],
  peek: [totalKills, headshotPercentage, crosshairPlacement, averageCounterStrafing, averageTimeToKill],
  hold: [totalKills, headshotPercentage, averageCounterStrafing, averageReactionTime, averageTimeToKill],
  aim: [totalKills, accuracyPercentage, killsPerMinute],
  awp: [totalKills, accuracyPercentage, averageReactionTime],
  recoil: [totalKills, accuracyPercentage],
  movement: [],
};

function relevantMetrics(task) {
  const category = findModCategory(task);

  let metrics = null;
  if (!category || !METRICS_MAPPING[category.slug]) {
    metrics = METRICS_MAPPING._default;
  } else {
    metrics = METRICS_MAPPING[category.slug];
  }

  return metrics.flatMap((metric) => metric(task.statistics[0]) ?? []);
}

function findModCategory(task) {
  let { mod } = task;
  if (task.mod.name === 'CreatorStudio') {
    mod = task.custom_set.game_mode;
  }

  // NOTE(ssaunier): Create an ad-hoc mod fron the task's name (Bootcamp legacy)
  if (!mod) {
    mod = { name: task.name };
  }

  const taskModName = mod.name?.toLowerCase();
  if (taskModName?.length > 0) {
    for (const category of props.modCategories) {
      for (const m of category.mods) {
        if (m.name.toLowerCase() === taskModName) {
          return category;
        }
        const children = m.children ?? [];
        for (const child of children) {
          if (child.name.toLowerCase() === taskModName) {
            return category;
          }
        }
      }
    }
  }
}

function modHasElo(task) {
  let { mod } = task;
  if (task.mod.name === 'CreatorStudio') {
    mod = task.custom_set.game_mode;
  }

  if (!mod) {
    return false; // Assume no-elo for unregistered mod
  }

  const taskModName = mod.name?.toLowerCase();
  if (taskModName?.length > 0) {
    for (const category of props.modCategories) {
      for (const m of category.mods) {
        if (m.name.toLowerCase() === taskModName) {
          return m.has_elo;
        }
        const children = m.children ?? [];
        for (const child of children) {
          if (child.name.toLowerCase() === taskModName) {
            return m.has_elo; // Parent has_elo property.
          }
        }
      }
    }
  }

  return false; // Assume no-elo for unknown mod
}

function highScoreValue() {
  if (props.task.mod.friendly_name === 'Prefire' || props.task.mod.friendly_name === 'Rapid by Wooting') {
    return (props.task.high_score.score / 1000).toFixed(2);
  }
  return props.task.high_score.score;
}

function taskEloClass(task) {
  if (task.completion.elo_end > task.completion.elo_start) {
    return 'bg-success-900 text-success-400';
  } if (task.completion.elo_end == task.completion.elo_start) {
    return 'bg-gray-700 text-text-secondary';
  }
  return 'bg-danger-900 text-danger-400';
}

function botModeClass() {
  return 'bg-gray-700 text-text-secondary py-[1px] px-[4px] text-sm rounded';
}
</script>

<style scoped>
.task-row {
  border-top: 1px solid #111;
}

.task-row-metric-img {
  vertical-align: -3px
}
</style>
